import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';

import useAlert from '../hooks/useAlert';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { Supplier, SupplierBatchImportResponse, SupplierUpsertPayload } from '../types';
import { suppliersApi } from '../services/suppliers';
import {
  applyCnpjMask,
  formatSupplierPhoneDisplay,
  formatSupplierPhoneInput,
  isValidBrazilianPhone,
  isValidInternationalPhone,
  isValidCnpj,
  isValidEmail,
} from '../utils/supplierSelection';
import './Suppliers.css';

type SupplierFormState = SupplierUpsertPayload;

const EMPTY_FORM: SupplierFormState = {
  corporate_name: '',
  trade_name: '',
  contact_name: '',
  email: '',
  phone: '',
  cnpj: '',
  state_registration: '',
  municipal_registration: '',
  legal_nature: '',
  opening_date: '',
  registration_status: '',
  system_registration_date: '',
  notes: '',
  is_checklist: false,
  is_si_high: false,
  is_international: false,
};

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [form, setForm] = useState<SupplierFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<SupplierBatchImportResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { showAlert, AlertComponent } = useAlert();
  const { showConfirm } = useModal();
  const { user } = useAuth();
  const PAGE_SIZE = 10;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const loadSuppliers = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const data = await suppliersApi.list({
        search: debouncedSearch.trim(),
        skip: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        signal,
      });
      setSuppliers(data.items);
      setTotalItems(data.total);
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      showAlert({
        title: 'Erro',
        message: error?.response?.data?.detail || 'Não foi possível carregar fornecedores.',
        variant: 'danger',
      });
      setSuppliers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, showAlert]);

  useEffect(() => {
    const controller = new AbortController();
    void loadSuppliers(controller.signal);
    return () => controller.abort();
  }, [loadSuppliers]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = totalItems === 0 ? 0 : (safeCurrentPage - 1) * PAGE_SIZE;
  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    let start = Math.max(1, safeCurrentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [safeCurrentPage, totalPages]);

  const openCreateModal = () => {
    setEditingSupplier(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setForm({
      corporate_name: supplier.corporate_name,
      trade_name: supplier.trade_name || '',
      contact_name: supplier.contact_name,
      email: supplier.email || '',
      phone: formatSupplierPhoneDisplay(supplier.phone, !!supplier.is_international),
      cnpj: supplier.cnpj || '',
      state_registration: supplier.state_registration || '',
      municipal_registration: supplier.municipal_registration || '',
      legal_nature: supplier.legal_nature || '',
      opening_date: supplier.opening_date || '',
      registration_status: supplier.registration_status || '',
      system_registration_date: supplier.system_registration_date || '',
      notes: supplier.notes || '',
      is_checklist: supplier.is_checklist,
      is_si_high: supplier.is_si_high,
      is_international: !!supplier.is_international,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingSupplier(null);
    setForm(EMPTY_FORM);
  };

  const validateForm = (): string | null => {
    if (!form.corporate_name?.trim()) return 'Razão social é obrigatória.';
    if (!form.contact_name?.trim()) return 'Nome do contato é obrigatório.';
    if (!form.email?.trim()) return 'E-mail é obrigatório.';
    if (!isValidEmail(form.email)) return 'E-mail inválido.';
    if (form.phone) {
      if (form.is_international && !isValidInternationalPhone(form.phone)) {
        return 'Telefone internacional deve ter entre 8 e 15 dígitos (com DDI).';
      }
      if (!form.is_international && !isValidBrazilianPhone(form.phone)) {
        return 'Telefone deve ter 10 ou 11 dígitos (DDD + número), ou 12/13 com DDI 55.';
      }
    }
    if (!form.is_international) {
      if (!form.cnpj?.trim()) return 'CNPJ é obrigatório.';
      if (!isValidCnpj(form.cnpj)) return 'CNPJ inválido.';
    }
    return null;
  };

  const saveSupplier = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      showAlert({ title: 'Validação', message: validationError, variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const isInternational = !!form.is_international;
      const payload: SupplierUpsertPayload = {
        corporate_name: form.corporate_name.trim(),
        trade_name: form.trade_name?.trim() || undefined,
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || undefined,
        cnpj: isInternational ? undefined : form.cnpj?.trim(),
        state_registration: isInternational ? undefined : form.state_registration?.trim() || undefined,
        municipal_registration: isInternational ? undefined : form.municipal_registration?.trim() || undefined,
        legal_nature: isInternational ? undefined : form.legal_nature?.trim() || undefined,
        opening_date: isInternational ? undefined : form.opening_date?.trim() || undefined,
        registration_status: isInternational ? undefined : form.registration_status?.trim() || undefined,
        system_registration_date: form.system_registration_date?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
        is_checklist: !!form.is_checklist,
        is_si_high: !!form.is_si_high,
        is_international: isInternational,
      };

      if (editingSupplier) {
        await suppliersApi.update(editingSupplier.id, payload);
      } else {
        await suppliersApi.create(payload);
      }

      closeModal();
      await loadSuppliers();
      showAlert({
        title: 'Sucesso',
        message: editingSupplier ? 'Fornecedor atualizado com sucesso.' : 'Fornecedor cadastrado com sucesso.',
        variant: 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error?.response?.data?.detail || 'Não foi possível salvar fornecedor.',
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteSupplier = async (supplier: Supplier) => {
    const confirmed = await showConfirm({
      title: 'Excluir fornecedor',
      message: `Deseja realmente excluir o fornecedor "${supplier.corporate_name}"?`,
      details: 'Essa ação desativa o fornecedor e ele não aparecerá nas listagens padrão.',
      variant: 'warning',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    try {
      await suppliersApi.remove(supplier.id);
      await loadSuppliers();
      showAlert({ title: 'Sucesso', message: 'Fornecedor excluído com sucesso.', variant: 'success' });
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error?.response?.data?.detail || 'Não foi possível excluir fornecedor.',
        variant: 'danger',
      });
    }
  };

  const canManageSupplier = useCallback((supplier: Supplier) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'analista') return true;
    return supplier.created_by === user.id;
  }, [user]);

  const canImportSuppliers = user?.role === 'admin' || user?.role === 'analista';

  const closeImportModal = () => {
    if (importing) return;
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImportSuppliers = async () => {
    if (!importFile) {
      showAlert({
        title: 'Validação',
        message: 'Selecione um arquivo Excel para importar.',
        variant: 'warning',
      });
      return;
    }

    try {
      setImporting(true);
      const result = await suppliersApi.importBatch(importFile);
      setImportResult(result);
      await loadSuppliers();
      showAlert({
        title: result.errors > 0 ? 'Importação concluída com ressalvas' : 'Importação concluída',
        message: `Criados: ${result.created}, pulados: ${result.skipped}, erros: ${result.errors}.`,
        variant: result.errors > 0 ? 'warning' : 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro',
        message: error?.response?.data?.detail || 'Não foi possível importar fornecedores.',
        variant: 'danger',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-3">
      <AlertComponent />
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h4 className="mb-0">Fornecedores</h4>
            <div className="d-flex gap-2">
              {canImportSuppliers && (
                <Button variant="outline-secondary" onClick={() => setShowImportModal(true)}>
                  Importar Excel
                </Button>
              )}
              <Button onClick={openCreateModal}>Novo Fornecedor</Button>
            </div>
          </div>

          <Row className="g-2 mb-3">
            <Col md={8}>
              <Form.Control
                type="text"
                placeholder="Buscar por razão social, contato, e-mail ou CNPJ"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              Carregando fornecedores...
            </div>
          ) : (
            <>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Razão Social</th>
                  <th>Contato</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>CNPJ</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Nenhum fornecedor encontrado.
                    </td>
                  </tr>
                ) : (
                  suppliers.map((supplier) => (
                    <tr key={supplier.id}>
                      <td>
                        {supplier.corporate_name}
                        {supplier.is_international && <Badge bg="secondary" className="ms-2">Internacional</Badge>}
                        {supplier.is_checklist && <Badge bg="info" className="ms-2">Checklist</Badge>}
                        {supplier.is_si_high && <Badge bg="warning" text="dark" className="ms-2">SI Alto</Badge>}
                      </td>
                      <td>{supplier.contact_name}</td>
                      <td>{supplier.email || '-'}</td>
                      <td>{formatSupplierPhoneDisplay(supplier.phone, supplier.is_international) || '-'}</td>
                      <td>{supplier.is_international ? '—' : (supplier.cnpj || '-')}</td>
                      <td className="d-flex gap-2">
                        {canManageSupplier(supplier) ? (
                          <>
                            <Button size="sm" variant="outline-primary" onClick={() => openEditModal(supplier)}>
                              Editar
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => void deleteSupplier(supplier)}>
                              Excluir
                            </Button>
                          </>
                        ) : (
                          <small className="text-muted">Sem permissão</small>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            {totalItems > 0 && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <small className="text-muted">
                  Exibindo {pageStart + 1}-
                  {Math.min(pageStart + PAGE_SIZE, totalItems)} de {totalItems}
                </small>
                <Pagination className="mb-0">
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={safeCurrentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={safeCurrentPage === 1}
                  />
                  {visiblePages.map((page) => (
                    <Pagination.Item key={page} active={page === safeCurrentPage} onClick={() => setCurrentPage(page)}>
                      {page}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={safeCurrentPage >= totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safeCurrentPage >= totalPages}
                  />
                </Pagination>
              </div>
            )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        fullscreen="sm-down"
        dialogClassName="supplier-form-modal"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</Modal.Title>
        </Modal.Header>
        <Form id="supplier-form" onSubmit={saveSupplier}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="supplier-international-toggle"
                  label="Fornecedor internacional (sem CNPJ)"
                  checked={!!form.is_international}
                  onChange={(event) =>
                    setForm((prev) => {
                      const isInternational = event.target.checked;
                      return {
                        ...prev,
                        is_international: isInternational,
                        phone: prev.phone
                          ? formatSupplierPhoneInput(prev.phone, isInternational)
                          : prev.phone,
                        ...(isInternational
                          ? {
                              cnpj: '',
                              state_registration: '',
                              municipal_registration: '',
                              legal_nature: '',
                              opening_date: '',
                              registration_status: '',
                            }
                          : {}),
                      };
                    })
                  }
                />
              </Col>
              <Col md={12}>
                <Form.Label>Razão social *</Form.Label>
                <Form.Control
                  value={form.corporate_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, corporate_name: event.target.value }))}
                  required
                />
              </Col>
              <Col md={12}>
                <Form.Label>Nome fantasia</Form.Label>
                <Form.Control
                  value={form.trade_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, trade_name: event.target.value }))}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Nome do contato *</Form.Label>
                <Form.Control
                  value={form.contact_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, contact_name: event.target.value }))}
                  required
                />
              </Col>
              <Col md={12}>
                <Form.Label>E-mail *</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  required
                />
              </Col>
              <Col md={form.is_international ? 12 : 6}>
                <Form.Label>
                  {form.is_international ? 'Telefone internacional (com DDI)' : 'Telefone'}
                </Form.Label>
                <Form.Control
                  value={form.phone}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      phone: formatSupplierPhoneInput(event.target.value, !!prev.is_international),
                    }))
                  }
                  maxLength={form.is_international ? 25 : undefined}
                  placeholder={
                    form.is_international
                      ? '+1 415 555 0100'
                      : '(00) 00000-0000 ou +55 (00) 00000-0000'
                  }
                />
              </Col>
              {!form.is_international && (
                <>
                  <Col md={6}>
                    <Form.Label>CNPJ *</Form.Label>
                    <Form.Control
                      value={form.cnpj}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, cnpj: applyCnpjMask(event.target.value) }))
                      }
                      placeholder="00.000.000/0000-00"
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Inscrição estadual</Form.Label>
                    <Form.Control
                      value={form.state_registration}
                      onChange={(event) => setForm((prev) => ({ ...prev, state_registration: event.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Inscrição municipal</Form.Label>
                    <Form.Control
                      value={form.municipal_registration}
                      onChange={(event) => setForm((prev) => ({ ...prev, municipal_registration: event.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Natureza jurídica</Form.Label>
                    <Form.Control
                      value={form.legal_nature}
                      onChange={(event) => setForm((prev) => ({ ...prev, legal_nature: event.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Data de abertura</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.opening_date || ''}
                      onChange={(event) => setForm((prev) => ({ ...prev, opening_date: event.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Situação cadastral</Form.Label>
                    <Form.Control
                      value={form.registration_status}
                      onChange={(event) => setForm((prev) => ({ ...prev, registration_status: event.target.value }))}
                    />
                  </Col>
                </>
              )}
              <Col md={6}>
                <Form.Label>Data cadastro no sistema</Form.Label>
                <Form.Control
                  type="date"
                  value={form.system_registration_date || ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, system_registration_date: event.target.value }))}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Observações gerais</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="supplier-checklist"
                  label="Fornecedor com checklist"
                  checked={!!form.is_checklist}
                  onChange={(event) => setForm((prev) => ({ ...prev, is_checklist: event.target.checked }))}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="supplier-si-high"
                  label="Fornecedor SI Alto"
                  checked={!!form.is_si_high}
                  onChange={(event) => setForm((prev) => ({ ...prev, is_si_high: event.target.checked }))}
                />
              </Col>
            </Row>
          </Modal.Body>
        </Form>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="supplier-form" variant="primary" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={closeImportModal} centered>
        <Modal.Header closeButton={!importing}>
          <Modal.Title>Importar fornecedores</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Selecione um arquivo Excel (.xlsx ou .xls)</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              disabled={importing}
              onChange={(event) => {
                const inputElement = event.target as HTMLInputElement;
                const selectedFile = inputElement.files?.[0] || null;
                setImportFile(selectedFile);
                setImportResult(null);
              }}
            />
            <Form.Text className="text-muted">
              A coluna CONTRATADA será importada como razão social. Em CNPJ, use o valor
              &quot;Internacional&quot; (ou uma coluna INTERNACIONAL) para fornecedores sem CNPJ.
            </Form.Text>
          </Form.Group>

          {importFile && (
            <p className="mt-3 mb-0">
              <strong>Arquivo selecionado:</strong> {importFile.name}
            </p>
          )}

          {importResult && (
            <div className="mt-3">
              <Alert variant={importResult.errors > 0 ? 'warning' : 'success'} className="mb-2">
                Processados: {importResult.total_rows} | Criados: {importResult.created} | Pulados: {importResult.skipped} | Erros: {importResult.errors}
              </Alert>
              {importResult.error_details.length > 0 && (
                <div style={{ maxHeight: 220, overflow: 'auto' }}>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Razão social</th>
                        <th>Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.error_details.map((item, index) => (
                        <tr key={`${item.row}-${index}`}>
                          <td>{item.row}</td>
                          <td>{item.corporate_name || '-'}</td>
                          <td>{item.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeImportModal} disabled={importing}>
            Fechar
          </Button>
          <Button variant="primary" onClick={() => void handleImportSuppliers()} disabled={!importFile || importing}>
            {importing ? 'Importando...' : 'Importar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Suppliers;
